import { API_ROUTES } from "@constants/api-routes";
import { apiService, CONTACTS_TIMEOUT, HISTORY_TIMEOUT } from "@services/axios";
import type { ISuccessResponse } from "../../../../types/api.types";
import {
  CampaignGroup,
  CampaignVisibleFieldsConfig,
  CopyHistoryItem,
  LeadContact,
} from "../types/lead-contacts.types";

export interface LeadContactsResponse {
  contacts: LeadContact[];
  groupedByCampaign: Record<string, CampaignGroup>;
  /** Campanhas do tipo Controle de Qualidade (upload_status = 'QUALIDADE'), agrupadas fora da campanha original. */
  qualityGroupedByCampaign?: Record<string, CampaignGroup>;
  hasMore: boolean;
  totalPages: number;
}

export interface CampaignContactsResponse {
  contacts: LeadContact[];
  hasMore: boolean;
  campaignTotal: number;
}

export interface CopiedIdsResponse {
  copiedLeadIds: number[];
}

export interface CopyHistoryFilters {
  leadId?: number;
  coordId?: number;
  campaignName?: string;
  origin?: "normal" | "repescagem";
}

export interface CopyHistoryResponse {
  history: CopyHistoryItem[];
  total?: number;
  hasMore?: boolean;
  page?: number;
  limit?: number;
}

interface CopyHistoryParams {
  limit: number;
  offset: number;
  leadId?: number;
  coordId?: number;
  campaignName?: string;
  origin?: "normal" | "repescagem";
}

export class LeadContactsService {
  static async getLeads(
    companyId: number,
    page: number = 1,
    limit: number = 5,
  ): Promise<LeadContactsResponse> {
    const response = await apiService.getWithTimeout<LeadContactsResponse>(
      API_ROUTES.CONTACTS.GET_LEADS,
      CONTACTS_TIMEOUT,
      {
        params: { companyId, page, limit },
      },
    );

    if (response.status === "success") {
      const data = (response as ISuccessResponse<LeadContactsResponse>).data;
      return {
        contacts: data.contacts || [],
        groupedByCampaign: data.groupedByCampaign || {},
        qualityGroupedByCampaign: data.qualityGroupedByCampaign || {},
        hasMore: data.hasMore || false,
        totalPages: data.totalPages || 0,
      };
    }

    throw new Error("Erro na resposta da API");
  }

  static async getLeadsByCampaign(
    companyId: number,
    campaignName: string,
    page: number = 1,
    limit: number = 10,
  ): Promise<CampaignContactsResponse> {
    const response = await apiService.getWithTimeout<CampaignContactsResponse>(
      API_ROUTES.CONTACTS.GET_LEADS_BY_CAMPAIGN,
      CONTACTS_TIMEOUT,
      {
        params: { companyId, campaignName, page, limit },
      },
    );

    if (response.status === "success") {
      const data = (response as ISuccessResponse<CampaignContactsResponse>)
        .data;
      return {
        contacts: data.contacts || [],
        hasMore: data.hasMore || false,
        campaignTotal: data.campaignTotal || 0,
      };
    }

    throw new Error("Erro na resposta da API");
  }

  static async getCopiedIds(): Promise<CopiedIdsResponse> {
    const response = await apiService.get<CopiedIdsResponse>(
      API_ROUTES.LEAD_COPY.COPIED_IDS,
    );

    if (response.status === "success") {
      const data = (response as ISuccessResponse<CopiedIdsResponse>).data;
      return {
        copiedLeadIds: data.copiedLeadIds || [],
      };
    }

    throw new Error("Erro ao carregar leads copiados");
  }

  static async getCopyHistory(
    filters?: CopyHistoryFilters,
    page: number = 1,
    limit: number = 20,
  ): Promise<CopyHistoryResponse> {
    // Validar valores de paginação
    const validLimit = Math.min(Math.max(limit, 1), 100);
    const validPage = Math.max(1, page);
    const calculatedOffset = (validPage - 1) * validLimit;

    // Limitar offset máximo para trabalhar apenas com as últimas 1000 cópias
    // O histórico mostra apenas as últimas 1000 cópias
    const MAX_HISTORY_RECORDS = 1000;
    const MAX_OFFSET = Math.max(0, MAX_HISTORY_RECORDS - validLimit); // Máximo baseado no limite de registros
    const validOffset = Math.min(Math.max(calculatedOffset, 0), MAX_OFFSET);

    // Se o offset foi limitado, ajustar página para refletir o offset real
    const actualPage =
      validOffset < calculatedOffset
        ? Math.floor(validOffset / validLimit) + 1
        : validPage;

    const params: CopyHistoryParams = {
      limit: validLimit,
      offset: validOffset,
    };

    if (filters?.leadId) {
      params.leadId = filters.leadId;
    }
    if (filters?.coordId) {
      params.coordId = filters.coordId;
    }
    if (filters?.campaignName) {
      params.campaignName = filters.campaignName;
    }
    if (filters?.origin) {
      params.origin = filters.origin;
    }

    interface CopyHistoryApiData {
      history: CopyHistoryItem[];
      total: number;
      hasMore: boolean;
    }

    const response = await apiService.getWithTimeout<CopyHistoryApiData>(
      API_ROUTES.LEAD_COPY.HISTORY,
      HISTORY_TIMEOUT,
      { params },
    );

    if (response.status === "success") {
      const successResponse = response as ISuccessResponse<CopyHistoryApiData>;
      const data = successResponse.data;

      // Se o offset foi limitado, ajustar hasMore baseado no offset real usado
      const adjustedHasMore =
        data?.hasMore &&
        validOffset < MAX_OFFSET &&
        data.total >= MAX_HISTORY_RECORDS;

      return {
        history: data?.history || [],
        total: data?.total || 0,
        hasMore: adjustedHasMore || false,
        page: actualPage || validPage,
        limit: validLimit,
      };
    }

    throw new Error("Erro ao carregar histórico de cópias");
  }

  static async recordCopy(
    leadId: number,
    copyType: "individual" | "batch",
  ): Promise<void> {
    await apiService.post(API_ROUTES.LEAD_COPY.RECORD, {
      leadId,
      copyType,
    });
  }

  static async recordBatchCopy(
    leadIds: number[],
    copyType: "batch",
  ): Promise<void> {
    await apiService.post(API_ROUTES.LEAD_COPY.RECORD_BATCH, {
      leadIds,
      copyType,
    });
  }

  /** Campos extras da campanha a partir do CRM (PostgreSQL campaigns_campaign_config). */
  static async getCampaignExtraFields(
    companyId: number,
    campaignId: number,
  ): Promise<Array<{ field_name: string; field_type: string }>> {
    const response = await apiService.getWithTimeout<
      Array<{ field_name: string; field_type: string }>
    >(
      API_ROUTES.CONTACTS.GET_CAMPAIGN_EXTRA_FIELDS(campaignId),
      CONTACTS_TIMEOUT,
      { params: { companyId } },
    );
    if (response.status === "success") {
      const data = (
        response as ISuccessResponse<
          Array<{ field_name: string; field_type: string }>
        >
      ).data;
      return Array.isArray(data) ? data : [];
    }
    throw new Error("Erro ao carregar campos extras da campanha");
  }

  /** Configuração de campos visíveis por campanha (cópia/visualização). Coordenadores leem; admin altera. */
  static async getCampaignVisibleFields(
    companyId: number,
  ): Promise<CampaignVisibleFieldsConfig[]> {
    const response = await apiService.getWithTimeout<
      CampaignVisibleFieldsConfig[]
    >(API_ROUTES.CONTACTS.GET_CAMPAIGN_VISIBLE_FIELDS, CONTACTS_TIMEOUT, {
      params: { companyId },
    });
    if (response.status === "success") {
      const data = (response as ISuccessResponse<CampaignVisibleFieldsConfig[]>)
        .data;
      return Array.isArray(data) ? data : [];
    }
    throw new Error("Erro ao carregar configuração de campos visíveis");
  }

  /** Atualiza campos visíveis de uma campanha. Apenas administrador. */
  static async setCampaignVisibleFields(
    companyId: number,
    campaignId: number,
    payload: {
      visible_fields: string[];
      field_labels?: Record<string, string>;
      extra_fields?: Array<{ key: string; label: string }>;
      quality_fields?: Array<{ key: string; label: string }>;
      field_order?: string[];
      campaign_name?: string;
    },
  ): Promise<CampaignVisibleFieldsConfig> {
    const response = await apiService.put<CampaignVisibleFieldsConfig>(
      API_ROUTES.CONTACTS.SET_CAMPAIGN_VISIBLE_FIELDS(campaignId),
      { ...payload, company_id: companyId },
    );
    if (response.status === "success") {
      const data = (response as ISuccessResponse<CampaignVisibleFieldsConfig>)
        .data;
      return data!;
    }
    throw new Error("Erro ao salvar configuração de campos visíveis");
  }
}
